/**
 * AgentHarnessSelector — Node editor "Agent Harness" tab for Agent actions.
 *
 * Matches the LA1 Agent Harness tab UX:
 *   - Info banner explaining the feature
 *   - Execution Environment section with Harness Type dropdown
 *   - Sandbox Configuration section with dropdown + Ready/status indicator
 *   - Repository Skills section (repo dropdown + skills folder path)
 *
 * Input Files have moved to the Parameters tab (with token picker support).
 *
 * Writes parameters under agentModelSettings.agentHarness.* per the design doc:
 *   parameterValues['agentModelSettings.agentHarness.type']
 *   parameterValues['agentModelSettings.agentHarness.sandboxConfigurationName']
 *   parameterValues['agentModelSettings.agentHarness.skills']
 */

import { useMemo, useCallback, useState } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Button,
  Input,
  Field,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Dismiss16Regular,
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  ArrowSync16Regular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useSandboxConfigs } from '@/hooks/useSandboxConfigs';
import { SandboxConfiguration, AgentHarnessType, AgentHarnessSkill } from '@/types/sandbox';
import { SandboxProvisioningState } from '@/types/sandbox';

// =============================================================================
// CONSTANTS
// =============================================================================

const HARNESS_TYPES: { value: AgentHarnessType; label: string }[] = [
  { value: AgentHarnessType.GHCP, label: 'GHCP (GitHub Copilot)' },
];

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    minWidth: 0,
    overflowX: 'hidden',
  },

  // Section
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  sectionDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
    wordBreak: 'break-word' as const,
  },
  infoBanner: {
    maxWidth: '100%',
    '& *': {
      whiteSpace: 'normal' as const,
      wordBreak: 'break-word' as const,
    },
  },
  optionalTag: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
  },

  // Sandbox status indicator (shown below the dropdown)
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
  },
  statusReady: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusBuilding: {
    color: tokens.colorPaletteBlueForeground2,
  },
  statusFailed: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  snapshotId: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  // Error banner
  errorBanner: {
    marginTop: tokens.spacingVerticalXS,
  },

  // Repository Skills
  skillCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  skillFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    flex: 1,
    minWidth: 0,
  },

  contentText: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  removeButton: {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    ...shorthands.padding('0'),
  },

  // Add button
  addButton: {
    alignSelf: 'flex-start',
    marginTop: tokens.spacingVerticalXS,
  },

  // Field overrides to stretch dropdowns full width
  fieldFull: {
    '& .fui-Dropdown': {
      width: '100%',
    },
  },
});

// =============================================================================
// HELPERS
// =============================================================================

function getStatusIcon(state?: SandboxProvisioningState) {
  switch (state) {
    case SandboxProvisioningState.Ready:
      return <CheckmarkCircle16Filled />;
    case SandboxProvisioningState.InProgress:
      return <ArrowSync16Regular />;
    case SandboxProvisioningState.Failed:
      return <DismissCircle16Filled />;
    default:
      return null;
  }
}

function getStatusClassName(state: SandboxProvisioningState | undefined, styles: ReturnType<typeof useStyles>) {
  switch (state) {
    case SandboxProvisioningState.Ready:
      return styles.statusReady;
    case SandboxProvisioningState.InProgress:
      return styles.statusBuilding;
    case SandboxProvisioningState.Failed:
      return styles.statusFailed;
    default:
      return '';
  }
}

/**
 * Parse the repository name from the URL.
 */
function parseRepoName(url: string): string {
  const parts = url.split('/');
  return parts[parts.length - 1] ?? url;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AgentHarnessSelector = () => {
  const styles = useStyles();

  const [showAddSkillRow, setShowAddSkillRow] = useState(false);
  const [newSkillRepoUrl, setNewSkillRepoUrl] = useState('');
  const [newSkillFolder, setNewSkillFolder] = useState('');

  const parameterValues = useNodeEditorStore((state) => state.parameterValues);
  const setParameter = useNodeEditorStore((state) => state.setParameter);

  const { data: sandboxConfigs = [], isLoading: isSandboxConfigsLoading } = useSandboxConfigs();

  // Current values from parameters
  const harnessType = parameterValues['agentModelSettings.agentHarness.type'] as AgentHarnessType | undefined;
  const selectedSandboxConfigName = parameterValues['agentModelSettings.agentHarness.sandboxConfigurationName'] as string | undefined;
  const repositorySkills = useMemo(
    () => (parameterValues['agentModelSettings.agentHarness.skills'] ?? []) as AgentHarnessSkill[],
    [parameterValues],
  );

  const selectedSandboxConfig = useMemo<SandboxConfiguration | null>(() => {
    if (!selectedSandboxConfigName) return null;
    return (
      sandboxConfigs.find((s) => s.name === selectedSandboxConfigName) ?? null
    );
  }, [selectedSandboxConfigName, sandboxConfigs]);

  const sandboxConfigRepos = useMemo(
    () => selectedSandboxConfig?.properties.repositorySettings?.repositories ?? [],
    [selectedSandboxConfig],
  );

  const sandboxConfigOptions = useMemo(() => {
    const readySandboxConfigs = sandboxConfigs.filter(
      (s) => s.properties.provisioningState === SandboxProvisioningState.Ready,
    );
    if (!selectedSandboxConfig || readySandboxConfigs.some((s) => s.name === selectedSandboxConfigName)) {
      return readySandboxConfigs;
    }
    return [...readySandboxConfigs, selectedSandboxConfig];
  }, [sandboxConfigs, selectedSandboxConfig, selectedSandboxConfigName]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleHarnessTypeChange = useCallback(
    (_: unknown, data: { optionValue?: string | undefined }) => {
      const value = data.optionValue as AgentHarnessType | undefined;
      if (!value) {
        setParameter('agentModelSettings.agentHarness.type', null);
        setParameter('agentModelSettings.agentHarness.sandboxConfigurationName', null);
        setParameter('agentModelSettings.agentHarness.skills', []);
        setParameter('agentModelSettings.agentHarness.inputFiles', []);
      } else {
        setParameter('agentModelSettings.agentHarness.type', value);
      }
    },
    [setParameter],
  );

  const handleSandboxChange = useCallback(
    (_: unknown, data: { optionValue?: string | undefined }) => {
      const sandboxName = data.optionValue;
      setParameter('agentModelSettings.agentHarness.sandboxConfigurationName', sandboxName ?? null);
      setParameter('agentModelSettings.agentHarness.skills', []);

      // Reset local add-skill form state
      setShowAddSkillRow(false);
      setNewSkillRepoUrl('');
      setNewSkillFolder('');
    },
    [setParameter],
  );

  const handleAddRepositorySkill = useCallback(() => {
    if (!newSkillRepoUrl || !newSkillFolder.trim()) return;
    const folder = newSkillFolder.trim();

    // Aggregate: if the repository already exists, append the folder to it
    const existingIndex = repositorySkills.findIndex(
      (s) => s.repository === newSkillRepoUrl,
    );

    let updated: AgentHarnessSkill[];
    if (existingIndex >= 0) {
      const existing = repositorySkills[existingIndex]!;
      // Skip if this exact folder is already listed
      if (existing.folders.includes(folder)) {
        setNewSkillRepoUrl('');
        setNewSkillFolder('');
        setShowAddSkillRow(false);
        return;
      }
      updated = repositorySkills.map((s, i) =>
        i === existingIndex ? { ...s, folders: [...s.folders, folder] } : s,
      );
    } else {
      updated = [
        ...repositorySkills,
        { repository: newSkillRepoUrl, folders: [folder] },
      ];
    }

    setParameter('agentModelSettings.agentHarness.skills', updated);
    setNewSkillRepoUrl('');
    setNewSkillFolder('');
    setShowAddSkillRow(false);
  }, [newSkillRepoUrl, newSkillFolder, repositorySkills, setParameter]);

  const handleRemoveRepositorySkill = useCallback(
    (index: number) => {
      const updated = repositorySkills.filter((_, i) => i !== index);
      setParameter('agentModelSettings.agentHarness.skills', updated);
    },
    [repositorySkills, setParameter],
  );

  const cancelAddSkillRow = useCallback(() => {
    setShowAddSkillRow(false);
    setNewSkillRepoUrl('');
    setNewSkillFolder('');
  }, []);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* Info Banner */}
      <MessageBar intent="info" className={styles.infoBanner}>
        <MessageBarBody>
          The Agent Harness configures the sandbox environment where the agent executes.
          It provisions an isolated compute container with tools and repos.
        </MessageBarBody>
      </MessageBar>

      {/* ── Execution Environment ────────────────────────────────────── */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>Execution Environment</Text>
        <Text className={styles.sectionDescription}>
          Choose the harness runtime for agent execution.
        </Text>

        <div className={styles.fieldFull}>
          <Field label="Harness Type">
            <Dropdown
              value={HARNESS_TYPES.find((t) => t.value === harnessType)?.label ?? ''}
              selectedOptions={harnessType ? [harnessType] : []}
              onOptionSelect={handleHarnessTypeChange}
              placeholder="Select harness type..."
              clearable
            >
              {HARNESS_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>
                  {t.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
      </div>

      {/* Remaining sections only appear once a harness type is selected */}
      {harnessType && (
        <>
        {/* ── Sandbox Configuration ────────────────────────────────────── */}
        <div className={styles.section}>
        <Text className={styles.sectionTitle}>
          Sandbox Configuration
          <span className={styles.optionalTag}>(Optional)</span>
        </Text>
        <Text className={styles.sectionDescription}>
          Link a pre-built sandbox with cloned repos from your Automation Project.
          If not set, a clean sandbox with the default base image is used.
        </Text>

        <div className={styles.fieldFull}>
          <Field label="Sandbox Configuration">
            {isSandboxConfigsLoading ? (
              <Spinner size="tiny" label="Loading sandbox configurations..." />
            ) : (
              <Dropdown
                value={selectedSandboxConfig ? selectedSandboxConfig.name : ''}
                selectedOptions={selectedSandboxConfigName ? [selectedSandboxConfigName] : []}
                onOptionSelect={handleSandboxChange}
                placeholder="(Default — base image)"
                clearable
              >
                {sandboxConfigOptions.map((sandboxConfig) => (
                  <Option key={sandboxConfig.name} value={sandboxConfig.name} text={sandboxConfig.name}>
                    {sandboxConfig.name}
                  </Option>
                ))}
              </Dropdown>
            )}
          </Field>

          {selectedSandboxConfig && (
            <div className={styles.statusRow}>
              <span className={getStatusClassName(selectedSandboxConfig.properties.provisioningState, styles)}>
                {getStatusIcon(selectedSandboxConfig.properties.provisioningState)}
              </span>
              <Text className={mergeClasses(
                styles.statusText,
                getStatusClassName(selectedSandboxConfig.properties.provisioningState, styles),
              )}>
                {selectedSandboxConfig.properties.provisioningState}
              </Text>
            </div>
          )}

          {/* Error banner for failed sandboxes */}
          {selectedSandboxConfig && selectedSandboxConfig.properties.provisioningState === SandboxProvisioningState.Failed && (
            <MessageBar intent="error" className={styles.errorBanner}>
              <MessageBarBody>
                {selectedSandboxConfig.properties.errorMessage ??
                  'Sandbox provisioning failed. Choose a different configuration or wait for it to rebuild.'}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>
      </div>

      {/* ── Repository Skills ────────────────────────────────────────── */}
      {selectedSandboxConfig && sandboxConfigRepos.length > 0 && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>
            Repository Skills
            <span className={styles.optionalTag}>(Optional)</span>
          </Text>
          <Text className={styles.sectionDescription}>
            Select a repository from the sandbox and specify the relative path to a skills folder
            containing SKILL.md files.
          </Text>

          {/* Existing skills */}
          {repositorySkills.map((skill, index) => (
            <div key={`${skill.repository}::${skill.folders.join(',')}`} className={styles.skillCard}>
              <div className={styles.skillFields}>
                <Text className={styles.contentText}>
                  {parseRepoName(skill.repository)} — {skill.folders.length > 0 ? skill.folders.join(', ') : ''}
                </Text>
              </div>
              <Button
                className={styles.removeButton}
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular />}
                onClick={() => handleRemoveRepositorySkill(index)}
                aria-label={`Remove skill ${skill.folders.length > 0 ? skill.folders.join(', ') : ''}`}
              />
            </div>
          ))}

          {/* Inline add skill row */}
          {showAddSkillRow && (
            <div className={styles.skillCard}>
              <div className={styles.skillFields}>
                <div className={styles.fieldFull}>
                  <Field label="Repository">
                    <Dropdown
                      value={newSkillRepoUrl ? parseRepoName(newSkillRepoUrl) : ''}
                      selectedOptions={newSkillRepoUrl ? [newSkillRepoUrl] : []}
                      onOptionSelect={(_, data) => setNewSkillRepoUrl(data.optionValue ?? '')}
                      placeholder="Select a repository..."
                      size="small"
                    >
                      {sandboxConfigRepos.map((repo) => (
                        <Option key={repo.url} value={repo.url} text={parseRepoName(repo.url)}>
                          {parseRepoName(repo.url)}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
                <Field label="Skills folder path">
                  <Input
                    size="small"
                    value={newSkillFolder}
                    onChange={(_, data) => setNewSkillFolder(data.value)}
                    placeholder="skills/code-review"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddRepositorySkill();
                      if (e.key === 'Escape') cancelAddSkillRow();
                    }}
                  />
                </Field>
              </div>
              <Button
                className={styles.removeButton}
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular />}
                onClick={cancelAddSkillRow}
                aria-label="Cancel add skill"
              />
            </div>
          )}

          {showAddSkillRow ? (
            <Button
              className={styles.addButton}
              appearance="primary"
              size="small"
              onClick={handleAddRepositorySkill}
              disabled={!newSkillRepoUrl || !newSkillFolder.trim()}
            >
              Add
            </Button>
          ) : (
            <Button
              className={styles.addButton}
              appearance="subtle"
              size="small"
              icon={<Add16Regular />}
              onClick={() => setShowAddSkillRow(true)}
            >
              Add skill
            </Button>
          )}
        </div>
      )}

      </>
      )}
    </div>
  );
};

AgentHarnessSelector.displayName = 'AgentHarnessSelector';
